import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { PlayCircleIcon, SunIcon, WifiIcon, ShieldIcon } from 'lucide-react';
const groups = [{
  name: 'Entertainment',
  icon: PlayCircleIcon,
  color: '#6366F1',
  position: 'top',
  items: ['Audio', 'Theatre', 'Streaming']
}, {
  name: 'Ambience',
  icon: SunIcon,
  color: '#F59E0B',
  position: 'right',
  items: ['Lighting', 'Shading', 'Climate']
}, {
  name: 'Infrastructure',
  icon: WifiIcon,
  color: '#3B82F6',
  position: 'bottom',
  items: ['Network', 'Control System', 'Power']
}, {
  name: 'Protection',
  icon: ShieldIcon,
  color: '#64748B',
  position: 'left',
  items: ['CCTV', 'Access Control', 'Alerts']
}];
export function HomeOS() {
  return <section className="py-32 bg-void relative border-t border-graphite overflow-hidden" data-id="element-407">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-408">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-24 md:mb-32" data-id="element-409">
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-410">
            Your Home Operating System
          </h2>
          <p className="text-silver max-w-2xl mx-auto text-lg" data-id="element-411">
            You are not buying AV equipment. You are upgrading to an operating
            system for living.
          </p>
        </motion.div>

        {/* Hub Diagram */}
        <div className="relative w-full max-w-4xl mx-auto aspect-square md:aspect-[2/1] flex items-center justify-center mb-24 md:mb-32" data-id="element-412">
          {/* Center Hub */}
          <motion.div className="relative z-20 w-32 h-32 md:w-40 md:h-40 rounded-full flex items-center justify-center" initial={{
          scale: 0
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} data-id="element-413">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cinema via-music to-social opacity-30 blur-2xl animate-pulse-glow" data-id="element-414" />
            <div className="absolute inset-0 rounded-full bg-carbon border border-graphite flex items-center justify-center shadow-2xl" data-id="element-415">
              <span className="font-serif text-xl tracking-widest text-platinum uppercase text-center leading-tight" data-id="element-416">
                QUBIX
                <br data-id="element-417" />
                <span className="text-sm text-silver" data-id="element-418">OS</span>
              </span>
            </div>
          </motion.div>

          {/* Group Nodes */}
          {groups.map((group, i) => {
          // Calculate positions based on cardinal directions
          let x = 0,
            y = 0;
          const distance = typeof window !== 'undefined' && window.innerWidth < 768 ? 140 : 250;
          if (group.position === 'top') y = -distance;
          if (group.position === 'bottom') y = distance;
          if (group.position === 'left') x = -distance;
          if (group.position === 'right') x = distance;
          const Icon = group.icon;
          return <Fragment key={group.name} data-id="element-419">
                {/* Connection Line */}
                <motion.svg className="absolute inset-0 w-full h-full pointer-events-none z-0" initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5 + i * 0.2,
              duration: 0.5
            }} data-id="element-420">
                  <line x1="50%" y1="50%" x2={`calc(50% + ${x}px)`} y2={`calc(50% + ${y}px)`} stroke={group.color} strokeWidth="2" strokeOpacity="0.4" strokeDasharray="6 6" data-id="element-421" />
                </motion.svg>

                {/* Group Card */}
                <motion.div className="absolute z-10 w-40 md:w-48" style={{
              left: `calc(50% + ${x}px)`,
              top: `calc(50% + ${y}px)`,
              transform: 'translate(-50%, -50%)'
            }} initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5 + i * 0.2,
              type: 'spring',
              stiffness: 100
            }} data-id="element-422">
                  <div className="bg-carbon border rounded-xl p-4 shadow-xl transition-all duration-300 hover:scale-105 group" style={{
                borderColor: `${group.color}40`
              }} data-id="element-423">
                    {/* Hover Glow */}
                    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 blur-md -z-10" style={{
                  backgroundColor: group.color
                }} data-id="element-424" />

                    <div className="flex items-center space-x-3 mb-3 border-b border-graphite pb-2" data-id="element-425">
                      <Icon className="w-5 h-5" style={{
                    color: group.color
                  }} data-id="element-426" />
                      <span className="font-serif text-platinum" data-id="element-427">
                        {group.name}
                      </span>
                    </div>

                    <ul className="space-y-1.5" data-id="element-428">
                      {group.items.map((item, idx) => <li key={idx} className="flex items-center text-xs text-silver" data-id="element-429">
                          <span className="w-1 h-1 rounded-full mr-2" style={{
                      backgroundColor: group.color
                    }} data-id="element-430" />
                          {item}
                        </li>)}
                    </ul>
                  </div>
                </motion.div>
              </Fragment>;
        })}
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 md:mt-0" data-id="element-431">
          {[{
          title: 'Connected Living',
          desc: 'Audio + Lighting + Control + Network + Security. One ecosystem.',
          color: 'text-cinema'
        }, {
          title: 'Invisible Intelligence',
          desc: 'AI learns your patterns and adapts automatically.',
          color: 'text-music'
        }, {
          title: 'Future-Proof',
          desc: 'Upgrade experiences, not hardware. Your home evolves with you.',
          color: 'text-relax'
        }].map((feature, i) => <motion.div key={i} className="bg-carbon border border-graphite p-8 rounded-2xl text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.2
        }} data-id="element-432">
              <h3 className={`font-serif text-xl mb-3 ${feature.color}`} data-id="element-433">
                {feature.title}
              </h3>
              <p className="text-silver leading-relaxed" data-id="element-434">{feature.desc}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}